import type { ColumnDef } from '@tanstack/react-table';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DEVICE_MODEL_LABELS } from '@/constants/device';
import { cn } from '@/lib/utils';
import type { IAutoTestLog } from '@/types/production/auto-test-log';

export type AutoTestLogRow = IAutoTestLog;

interface AutoTestLogTableProps {
  data: AutoTestLogRow[];
}

const statuses = {
  success: 'text-green-500 bg-green-800/20',
  error: 'text-rose-500 bg-rose-800/20',
};

const text = {
  success: 'text-green-800',
  error: 'text-rose-800',
};

export function AutoTestLogTable({ data }: AutoTestLogTableProps) {
  const autoTestLogColumns: ColumnDef<AutoTestLogRow>[] = [
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ row }) => {
        const log = row.original;
        const status = log.status ? 'success' : 'error';
        const label = log.status ? 'Sucesso' : 'Falha';
        return (
          <div className="flex items-center gap-1">
            <div className={cn(statuses[status], 'flex-none rounded-full p-1')}>
              <div className="h-1.5 w-1.5 rounded-full bg-current" />
            </div>
            <div className={cn('hidden font-semibold sm:block', text[status])}>{label}</div>
          </div>
        );
      },
    },
    {
      header: 'Serial',
      accessorKey: 'equipment.serial',
      cell: ({ row }) => {
        const log = row.original;
        return <p title={log.equipment.serial}>{log.equipment.serial || '--'}</p>;
      },
    },
    {
      header: 'ICCID',
      accessorKey: 'equipment.iccid',
      cell: ({ row }) => {
        const log = row.original;
        return <p title={log.equipment.iccid}>{log.equipment.iccid || '--'}</p>;
      },
    },
    {
      header: 'Tecnologia',
      accessorKey: 'technology.system_name',
      cell: ({ row }) => {
        const log = row.original;
        return (
          DEVICE_MODEL_LABELS[log.technology.system_name as keyof typeof DEVICE_MODEL_LABELS] ||
          log.technology.system_name ||
          '--'
        );
      },
    },
    {
      header: 'Firmware',
      accessorKey: 'equipment.firmware',
      cell: ({ row }) => {
        const log = row.original;
        return log.equipment.firmware || '--';
      },
    },
    {
      header: 'Data de criação',
      accessorKey: 'created_at',
      cell: ({ row }) => {
        const log = row.original;
        const date = new Date(log.created_at);
        return date.toLocaleString();
      },
    },
    {
      header: 'Ação',
      accessorKey: 'actions',
      cell: ({ row }) => {
        const log = row.original;
        return (
          <Link to={`/production/logs/auto-test/${log.id}`}>
            <Button variant="ghost" size="sm">
              <ExternalLink className="h-4 w-4" />
            </Button>
          </Link>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns: autoTestLogColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="w-full">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={autoTestLogColumns.length} className="h-24 text-center">
                  Nenhum registro encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
